<template>
  <article class="expert-review-stats">
    <ExpertReviewScore id="review-rating" :expert-review-data="expertReviewData" :api-status="apiStatus" />
    <ExpertReviewAuthor id="review-author" :expert-review-data="expertReviewData" :api-status="apiStatus" card-bg="white" />

    <div id="review-list" :class="['review-card', apiStatus === 'LOADING' ? 'skeleton-box' : 'white-card']">
      <ExpertReviewProsCons :expert-review-data="expertReviewData" />
      <a @click="redirectToExpertReview" class="expert-review-link button-as-link"> Read full review &rarr;</a>
    </div>
  </article>
</template>

<script setup lang="ts">
import { IExpertReview } from '../../foundation/interfaces/IExpertReview';
import { ApiStatusTypes } from '../../foundation/types/apiStatusTypes';
import ExpertReviewScore from '../expert-reviews/ExpertReviewScore.vue';
import ExpertReviewAuthor from '../expert-reviews/ExpertReviewAuthor.vue';
import ExpertReviewProsCons from '../expert-reviews/ExpertReviewProsCons.vue';
import { useUrlBuilder } from '../../composables/useUrlBuilder';
import { useCarComparisonStore } from '../../foundation/stores/carComparisonStore';
import { computed } from 'vue';
import { AnalyticsEventNames, AnalyticsEvents, AnalyticsEventTypes } from '../../foundation/enums/AnalyticsEvents';

const props = withDefaults(
  defineProps<{
    expertReviewData: Partial<IExpertReview>;
    apiStatus: ApiStatusTypes;
    carData: any;
  }>(),
  { apiStatus: 'LOADING' }
);

const urlBuilder = useUrlBuilder();
const carComparisonStore = useCarComparisonStore();

const expertReviewsPageUrl = computed(() => {
  return urlBuilder.getExpertReviewsUrl(
    props.carData.Vehicle_UID as string,
    props.expertReviewData.Id as string,
    `${props.carData?.Year_low}-${props.carData?.Year_high}`,
    props.carData?.Make,
    props.carData?.Model
  );
});

function addToAdobeAnalytics() {
  try {
    carComparisonStore.pushToAnalytics(
      props.carData.Vehicle_UID as string,
      AnalyticsEvents.NavigationLinkClick,
      AnalyticsEventNames.ReadFullReview,
      AnalyticsEventTypes.ReviewLink,
      expertReviewsPageUrl.value
    );
  } catch (error) {
    console.error('Fail to push data to analytics', error);
  }
}

function redirectToExpertReview() {
  addToAdobeAnalytics();
  window.location.href = expertReviewsPageUrl.value;
}
</script>

<style lang="scss" scoped>
.expert-review-stats {
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 8px;
  height: 100%;

  #review-rating {
    grid-area: review-rating;
  }

  #review-author {
    grid-area: review-author;
    min-width: 20rem;
  }

  #review-list {
    grid-area: review-list;
    align-items: start;
    padding-top: 1.25rem;
    padding-bottom: 3rem;

    display: flex;
    flex-direction: column;

    @media screen and (min-width: #{$breakpoint-lg}) {
      padding-bottom: 0;
      flex-direction: row;
    }
  }

  grid-template-areas:
    'review-rating review-rating review-rating'
    'review-author review-author review-author'
    'review-list   review-list   review-list';

  @media screen and (min-width: #{$breakpoint-md}) {
    grid-template-areas:
      'review-rating review-list review-list'
      'review-author review-list review-list';
    min-height: 15.75rem;
  }

  @media screen and (min-width: #{$breakpoint-xxl}) {
    padding: 0;
  }

  .review-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 1rem;

    &__score {
      border-radius: 50%;
      background: $racq-pale-blue;
      border: 1px solid $racq-pale-blue-border;
      min-width: 4.375rem;
      min-height: 4.375rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $racq-navy;
    }
  }
}

.expert-review-link {
  text-decoration: none;
  position: absolute;
  right: 1rem;
  bottom: 0.5rem;
  font-size: 1rem;
  font-family: $firasans-regular-font;
  color: $racq-blue;
}

.loading {
  * {
    opacity: 0;
  }
}
</style>
